/**
 * @author: lv.chen
 * @date: 2025/8/12
 * @desc: ReportPreviewModal组件
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { useToggle } from 'ahooks';
import { Modal, Button } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import ReportCard from '../ReportCard';
import ReportContent from '../ReportContent';
import { LANGUAGE_EN, LANGUAGE_CN } from '@home/_config/config';
import { MARKET_REPORT_CATEGORY_VALUE } from '../../MarketReports/constants';

import './index.scss';

interface IReportPreviewModalProps extends IBaseProps {
  disabledShare?: boolean;
  report: IReportPartial | null;
  visible: boolean;
  onClose: () => void;
  onShare: () => void;
}

const ReportPreviewModal: React.FC<IReportPreviewModalProps> = (props) => {
  const { style, className, disabledShare = false, report, visible, onClose, onShare } = props;

  const { formatMessage } = useIntl();

  const [locale, { toggle }] = useToggle(LANGUAGE_EN, LANGUAGE_CN);
  const isCN = locale === LANGUAGE_CN;

  const switcherTextLocaleKey = locale === LANGUAGE_CN ? 'dbu_common_switch_en' : 'dbu_common_switch_cn';
  const reportCNFileKey = report?.file_name || '';
  const reportENFileKey = report?.file_name_en || '';
  const reportFileKeyDefault = reportCNFileKey || reportENFileKey;
  const reportFileKey = (locale === LANGUAGE_CN ? reportCNFileKey : reportENFileKey) || reportFileKeyDefault;
  const showCreatorInfo = report?.system !== MARKET_REPORT_CATEGORY_VALUE;

  return (
    <Modal
      style={style}
      className={classnames('dbu-report-preview-modal', className)}
      caption={
        <div className="dbu-report-preview-modal__title">
          <span>{formatMessage({ id: 'dbu_common_preview' })}</span>
          <div
            className="dbu-report-preview-modal__switcher"
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
          >
            <SvgIcon className="dbu-report-preview-modal__switcher-icon" type={isCN ? 'chinese' : 'english'} />
            <span>{formatMessage({ id: switcherTextLocaleKey })}</span>
          </div>
        </div>
      }
      size={1200}
      visible={visible}
      onClose={onClose}
    >
      <Modal.Body>
        <div className="dbu-report-preview-card">
          <h3 className="dbu-report-preview-card__title">
            {formatMessage({ id: 'dbu_report_preview_summary_card_title' })}
          </h3>
          <div className="dbu-report-preview-card__body">
            <ReportCard locale={locale} showCreatorInfo={showCreatorInfo} report={report} />
          </div>
        </div>
        <div className="dbu-report-preview-card">
          <h3 className="dbu-report-preview-card__title">
            {formatMessage({ id: 'dbu_report_preview_content_card_title' })}
          </h3>
          <div className="dbu-report-preview-card__body">
            <ReportContent reportFileKey={reportFileKey} />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          disabled={disabledShare}
          onClick={(e) => {
            e?.stopPropagation();
            onShare();
          }}
        >
          {formatMessage({ id: 'dbu_common_publish' })}
        </Button>
        <Button
          onClick={(e) => {
            e?.stopPropagation();
            onClose();
          }}
        >
          {formatMessage({ id: 'dbu_report_preview_back_btn_text' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReportPreviewModal;
